// src/pages/Estoque/Dashboard/ListagemEstoqueMinimo.tsx
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Chip, IconButton, LinearProgress, Pagination, Paper, Snackbar, SnackbarCloseReason,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, alpha, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LayoutBaseDeDashboard } from '../../../shared/layouts';
import { Environment } from '../../../shared/environment';
import {
  GraficosEstoqueService,
  IProdutoEstoqueMinimo,
} from '../../../shared/services/api/estoque/GraficosEstoqueService';
import { LinhasEsqueletoTabela } from '../../../shared/components';

export const ListagemEstoqueMinimo: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(false);
  const [linhas, setLinhas] = useState<IProdutoEstoqueMinimo[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [openMsg, setOpenMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const pagina = useMemo(() => Number(searchParams.get('pagina') || '1'), [searchParams]);

  const handleCloseMsg = (
    _event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenMsg(false);
  };

  useEffect(() => {
    setIsLoading(true);

    GraficosEstoqueService.getEstoqueMinimo(pagina, Environment.LIMITE_DE_LINHASSMALL)
      .then((result) => {
        setIsLoading(false);

        if (result instanceof Error) {
          setLinhas([]);
          setTotalCount(0);
          setMsgType('error');
          setMsg(result.message);
          setOpenMsg(true);
          return;
        }

        setLinhas(result.data);
        setTotalCount(result.totalCount);
      });
  }, [pagina]);

  const formatQuantity = (value: number) => value.toLocaleString('pt-BR');

  const formatCurrency = (value: number) => value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setSearchParams({ pagina: newPage.toString() }, { replace: true });
  };

  return (
    <LayoutBaseDeDashboard barraDeFerramentas={<Box />}>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openMsg}
        autoHideDuration={6000}
        onClose={handleCloseMsg}
      >
        <Alert onClose={handleCloseMsg} severity={msgType} variant="filled" sx={{ width: '100%' }}>
          {msg}
        </Alert>
      </Snackbar>

      <Box sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <IconButton onClick={() => navigate(-1)} size="small">
            <ArrowBackIcon />
          </IconButton>

          <WarningAmberIcon sx={{ color: theme.palette.error.main }} />

          <Typography variant="h6" fontWeight="bold">
            Produtos em Estoque Mínimo
          </Typography>

          <Chip
            label={`${totalCount} produto${totalCount !== 1 ? 's' : ''}`}
            size="small"
            sx={{ bgcolor: alpha(theme.palette.error.main, 0.12), color: theme.palette.error.main, fontWeight: 'bold' }}
            variant="outlined"
          />
        </Box>

        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Produto</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>EAN</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Estoque Atual</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Estoque Mínimo</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Preço Venda</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Valor em Estoque</TableCell>
                </TableRow>
              </TableHead>

              {isLoading && linhas.length === 0 && (
                <TableBody>
                  <LinhasEsqueletoTabela
                    colunas={[
                      { width: '30%' },
                      { width: '80%' },
                      { width: '55%' },
                      { width: '50%', align: 'right' },
                      { width: '50%', align: 'right' },
                      { width: '55%', align: 'right' },
                      { width: '55%', align: 'right' },
                    ]}
                  />
                </TableBody>
              )}
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: 0 }}>
                      <LinearProgress sx={{ height: '2px' }} />
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading && linhas.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        Nenhum produto abaixo do estoque mínimo
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading && linhas.map((item, index) => (
                  <TableRow
                    key={item.idproduto}
                    hover
                    sx={{
                      bgcolor: item.quan <= 0
                        ? alpha(theme.palette.error.main, 0.06)
                        : alpha(theme.palette.warning.main, 0.06),
                    }}
                  >
                    <TableCell>{((pagina - 1) * Environment.LIMITE_DE_LINHASSMALL) + index + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 280 }}>
                        {item.descricao}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                        {item.ean || 'Não informado'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={formatQuantity(item.quan)}
                        size="small"
                        color={item.quan <= 0 ? 'error' : 'warning'}
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell align="right">{formatQuantity(item.estoqueminino)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.pvenda)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(item.valorestoque)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalCount > Environment.LIMITE_DE_LINHASSMALL && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 2,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Pagination
                page={pagina}
                count={Math.ceil(totalCount / Environment.LIMITE_DE_LINHASSMALL)}
                onChange={handlePageChange}
                color="primary"
                size="medium"
                shape="rounded"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
              />
            </Box>
          )}
        </Paper>
      </Box>
    </LayoutBaseDeDashboard>
  );
};
